// The authenticated admin boundary.
//
// This file deliberately holds ONLY auth: the `beforeLoad` bootstrap, the
// staff-role gate, and the no-access card. All console chrome (sidebar, top
// bar, command palette) lives in `~/components/AdminShell` — auth is the one
// part of the shell that must not churn while the UI is redesigned.

import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminShell } from '~/components/AdminShell';
import { ToastProvider } from '~/components/Toaster';
import { ApiError } from '~/lib/api';
import { appOrigin, appSignInUrl, getMe, isStaff, logout, type MeUser } from '~/lib/auth';

export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.ensureQueryData<MeUser>({
        queryKey: ['auth', 'me'],
        queryFn: ({ signal }) => getMe(signal),
        staleTime: 60_000,
      });
      return { me };
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Not signed in anywhere → unified sign-in (M1), carrying a redirect
        // back to the page they wanted. Halt this navigation forever.
        window.location.replace(appSignInUrl());
        await new Promise(() => {});
      }
      throw err;
    }
  },
  component: AuthedShell,
});

function AuthedShell() {
  const { me } = Route.useRouteContext();

  // Signed in but not staff → static no-access card, NOT a redirect (a
  // redirect to sign-in would loop: the cookie is valid, the ROLE is
  // missing). motiflink §6.1.
  if (!isStaff(me)) {
    return <NoAdminAccess me={me} />;
  }

  return (
    <ToastProvider>
      <AdminShell me={me}>
        <Outlet />
      </AdminShell>
    </ToastProvider>
  );
}

// Signed in, but the account holds no staff role. Static card — never a
// redirect (that loops), per motiflink §6.1. English-only like the rest of
// the admin chrome.
function NoAdminAccess({ me }: { me: MeUser }) {
  const [busy, setBusy] = useState(false);

  async function handleSignOut() {
    setBusy(true);
    try {
      await logout();
    } finally {
      window.location.assign(appSignInUrl(`${window.location.origin}/dashboard`));
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-admin-canvas p-6">
      <div className="w-full max-w-md rounded-2xl border border-admin-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-xl font-bold text-admin-900">No admin access</h1>
        <p className="mt-2 text-sm text-admin-600">
          You&rsquo;re signed in as <span className="font-medium">{me.email}</span>, but this
          account doesn&rsquo;t have staff access to the admin console.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <a
            href={appOrigin()}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
          >
            Go to the app
          </a>
          <button
            type="button"
            disabled={busy}
            onClick={handleSignOut}
            className="rounded-lg border border-admin-200 px-4 py-2 text-sm font-medium text-admin-700 transition-colors hover:bg-admin-100 disabled:opacity-50"
          >
            Sign in with a different account
          </button>
        </div>
      </div>
    </div>
  );
}
